import React from 'react'
import {
    Container, Table, Row, Col, Label, Input, Collapse, Button,
} from 'reactstrap'
import '../css/QuoteViewController.css'
import SimpleDropdown from '../view/DropDownView'
import {JustDate} from '../model/JustDate';
import {
    getDailySchedule,
    getDailyScheduleDependency,
    updateDailyScheduleWithBody,
    getAssignTo,
    getSortedAssignTo,
} from '../model/request/schedule'
import {
    Priority,
    Schedule,
} from '../model/Schedule'
import { EQUIPMENT_LIST } from '../model/Equipment'
import { TextNormalization } from '../model/TextNormalization'
import { getTeamNames, getTypes } from '../model/request/quotes'
import TeamDropdown from '../view/TeamDropdown'
import {
    getFilesFromManyQuoteIDs,
    downloadFile,
} from '../model/request/files'
import {
    InfoButton,
} from '../view/InfoButton'
import {
    getAsanteProjectManagers,
} from '../model/request/login'
import dateFormat from 'dateformat'
import { dateOffsetText } from '../model/Formatting'
import {
    IoIosArrowUp,
    IoIosArrowDown,
} from 'react-icons/io'
import JobScheduleFlowViewController from './JobScheduleFlowViewController'

// Column-key -> comparable-value extractor, one map per table mode (the
// two tables have entirely different columns). Dates use JustDate's own
// valueOf() (already relied on elsewhere in this file for sorting), text
// columns are lowercased for a case-insensitive compare. Only plain
// display columns are listed - interactive ones (Assign To, Priority,
// Download, Started/Completion checkboxes) are intentionally left out,
// same as QuotesViewController.js only makes its display columns clickable.
const UNGROUPED_SORT_EXTRACTORS = {
    equip: (t) => (t.equip ?? '').toLowerCase(),
    job_name: (t) => (t.job_name ?? '').toLowerCase(),
    customer_name: (t) => (t.customer_name ?? '').toLowerCase(),
    expected_ship_date: (t) => t.expected_ship_date.valueOf(),
    human_expected_duration_minutes: (t) => t.human_expected_duration_minutes ?? 0,
}
const GROUPED_SORT_EXTRACTORS = {
    job_name: (j) => (j.job_name ?? '').toLowerCase(),
    customer_name: (j) => (j.customer_name ?? '').toLowerCase(),
    task_done_count: (j) => j.task_done_count,
    task_started_count: (j) => j.task_started_count,
    task_count: (j) => j.task_count,
    task_left_count: (j) => j.task_left_count,
    expected_ship_date: (j) => j.expected_ship_date.valueOf(),
    production_state_date: (j) => j.production_state_date.valueOf(),
    last_updated_time: (j) => j.last_updated_time?.valueOf() ?? 0,
    team_name: (j) => (j.team_name ?? '').toLowerCase(),
}

function lastUpdatedTag(date) {
    if (!date) {
        return null
    }
    const hours = (new Date() - date) / 3600000
    if (hours <= 0.5) return '+15' // dark green - freshest
    if (hours <= 2) return '+3' // medium green
    if (hours <= 4) return '+1' // light green
    if (hours <= 8) return '0' // amber/yellow - the 4-8h zone
    if (hours <= 24) return '-1' // light red
    if (hours <= 48) return '-3' // medium red
    if (hours <= 168) return '-15' // dark red
    return '-31' // darkest red - stale beyond a week
}

function formatTime(minutes) {
    if (minutes < 60) {
        return '' + minutes.toFixed(0) + " minutes"
    } else {
        return (minutes / 60).toFixed(2) + " hours"
    }
}

class DailyScheduleViewController extends React.Component {

    // MARK: Override Functions

    constructor(props) {
        super(props)
        this.state = {
            'search_object': Object.assign({
                'search_task': 'All',
                'completion': 'Not Completed',
                'assign_to': 'All',
                'production_state': 'In Production',
                'pm': 'All',
                'team': 'All',
                'sign_type': 'All',
            }, props.searchTask),
            'task': [],
            'not_filter_task': [],
            'dependency_finished_task': {},
            'dependency_started_task': {},
            'files': {},
            'error_files': {},
            'assign_to': [],
            'teams': [],
            'sign_types': [],
            'project_managers': [],
            'show_dependency': 'All', // All, Completed, Started

            // Job Schedule (grouped-by-job view) - a separate render
            // function from the daily schedule above. Reads from
            // `full_task` (see updateFullTask() in componentDidMount), a
            // background-fetched dataset independent of the Equip/
            // Completion/Assign To dropdowns, so toggling it never touches
            // or is limited by those filters.
            'group_by_job': false,
            'expandedJobIds': {},
            'mountedJobIds': {},
            'full_task': [],

            // Column-header sort, client-side only (the full task/job list
            // is already loaded, no pagination) - same click/toggle/icon
            // behavior as QuotesViewController.js's order_by/reverse_order,
            // just not round-tripped through a server requery. Reset
            // whenever group_by_job toggles (see toggleGroupByJob below) -
            // the two tables' columns don't overlap.
            'sort_column': null,
            'sort_reverse': false,
        }
    }

    componentDidMount() {
        this.props.turnOnLoader()

        getAssignTo().then(
            (result) => {
                this.setState({
                    'assign_to': result.data.assign_to,
                })
            },
            (error) => {
                this.props.showError(error)
            }
        )

        getTypes().then(
            (result) => {
                this.setState({
                    // In-use only: /quote/signtypes returns retired types
                    // too for Asante accounts (getSignTypes only adds
                    // `where inuse` for everyone else), and everyone on this
                    // screen is Asante. Labels only, de-duped - two rows can
                    // share a label and the filter matches on label.
                    'sign_types': [...new Set(
                        result.data.types
                            .filter((type) => type.inuse)
                            .map((type) => type.label)
                    )],
                })
            },
            (error) => {
                this.props.showError(error)
            }
        )

        getTeamNames().then(
            (result) => {
                this.setState({
                    'teams': result.data.teams,
                })
            },
            (error) => {
                this.props.showError(error)
            }
        )

        getAsanteProjectManagers().then(
            (result) => {
                this.setState({
                    'project_managers': result.data.project_managers,
                })
            },
            (error) => {
                this.props.showError(error)
            }
        )

        getDailyScheduleDependency().then(
            (result) => {
                const job_finished_tasks = {}
                const job_started_tasks = {}
                for (const task of result.data.dependency) {
                    if (!(task.quote_id in job_finished_tasks)) {
                        job_finished_tasks[task.quote_id] = new Set()
                        job_started_tasks[task.quote_id] = new Set()
                    }
                    if (task.end_time) {
                        job_finished_tasks[task.quote_id].add(task.order_index)
                    }
                    if (task.start_time) {
                        job_started_tasks[task.quote_id].add(task.order_index)
                    }
                }

                this.setState({
                    'dependency_finished_task': job_finished_tasks,
                    'dependency_started_task': job_started_tasks,
                })

                this.props.turnOffLoader()
                this.updateResults(this.state.search_object)
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            }
        )

        this.updateFullTask()
    }

    /**
     * Background fetch (no loader) of every unshipped, production-eligible
     * task, independent of the Equip/Completion/Assign To dropdowns (forced
     * to "All" here) but still scoped to whatever Production State/PM/Team/
     * Sign Type are currently selected. Every dropdown change does a full page reload
     * (this.props.search()), which remounts this component and re-runs
     * componentDidMount, so this stays in sync with those
     * without any extra wiring. Backs Group By Job and the
     * next/previous/depends-on task lookups.
     */
    updateFullTask() {
        getDailySchedule({
            'production_state': this.state.search_object.production_state,
            'pm': this.state.search_object.pm,
            'team': this.state.search_object.team,
            'sign_type': this.state.search_object.sign_type,
        }).then(
            (result) => {
                const task = result.data.task.map((task) => {
                    task.expected_ship_date = new JustDate(task.expected_ship_date)
                    task.ready_for_production_date = new JustDate(task.ready_for_production_date)
                    task.in_production_date = new JustDate(task.in_production_date)
                    task.thermoformed_signs_received_date = new JustDate(task.thermoformed_signs_received_date)
                    return task
                })
                this.setState({'full_task': task})
            },
            (error) => {
                this.props.showError(error)
            }
        )
    }

    /**
     * Patches a task's changed fields into full_task by id, so Group By Job
     * and prev/next/depends-on text reflect edits made in the normal view
     * without needing a full refetch.
     * @param {string} id
     * @param {object} changes
     */
    patchFullTask(id, changes) {
        const fullTask = this.state.full_task
        const index = fullTask.findIndex((t) => t.id === id)
        if (index === -1) {
            return
        }
        Object.assign(fullTask[index], changes)
        this.setState({'full_task': fullTask})
    }

    filterDependency(task=null) {
        if (!task) {
            task = this.state.not_filter_task
        }

        if (this.state.show_dependency === 'Completed') {
            task = task.filter((x) => {
                for (let i = 0; i < x.dependency.length; i++) {
                    if (x.quote_id in this.state.dependency_finished_task) {
                        if (!this.state.dependency_finished_task[x.quote_id].has(x.dependency[i])) {
                            return false
                        }
                    } else {
                        return false
                    }
                }
                return true
            })
        }
        else if (this.state.show_dependency === 'Started') {
            task = task.filter((x) => {
                for (let i = 0; i < x.dependency.length; i++) {
                    if (x.quote_id in this.state.dependency_started_task) {
                        if (!this.state.dependency_started_task[x.quote_id].has(x.dependency[i]) &&
                            !this.state.dependency_finished_task[x.quote_id].has(x.dependency[i])) {
                            return false
                        }
                    } else {
                        return false
                    }
                }
                return true
            })
        }

        this.setState({
            'task': task,
        })
    }

    updateResults(searchObject) {
        this.props.turnOnLoader()
        getDailySchedule(searchObject).then(
            (result) => {
                this.getFiles(result.data.task.map((task, index) => task.quote_id))
                let task = result.data.task.map((task) => {
                    task.expected_ship_date = new JustDate(task.expected_ship_date)
                    task.ready_for_production_date = new JustDate(task.ready_for_production_date)
                    task.in_production_date = new JustDate(task.in_production_date)
                    task.thermoformed_signs_received_date = new JustDate(task.thermoformed_signs_received_date)
                    return task
                })

                this.setState({'not_filter_task': task})
                this.filterDependency(task)
                this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            }
        )
    }

    // MARK: Job Schedule (grouped-by-job view - separate render function
    // from the daily schedule above, backed by `full_task` instead of the
    // dropdown-filtered `task`)

    /**
     * Reads from full_task (see updateFullTask()), which is independent of
     * the Equip/Completion/Assign To dropdowns, so toggling this never
     * touches those filters or triggers a refetch.
     */
    toggleGroupByJob() {
        this.setState({
            'group_by_job': !this.state.group_by_job,
            'sort_column': null,
            'sort_reverse': false,
        })
    }

    /**
     * @param {string} key makeJobScheduleRows() keys this by quote_id (one
     * row per job there); makeRows() keys it by task.id, so each task row's
     * flow panel opens/closes independently, even within the same job.
     */
    toggleJob(key) {
        const expanded = !this.state.expandedJobIds[key]
        this.setState((state) => ({
            'expandedJobIds': {...state.expandedJobIds, [key]: expanded},
            'mountedJobIds': expanded
                ? {...state.mountedJobIds, [key]: true}
                : state.mountedJobIds,
        }))
    }

    makeJobScheduleRows() {
        const jobsById = {}
        const jobOrder = []
        this.state.full_task.forEach((task) => {
            if (!(task.quote_id in jobsById)) {
                jobsById[task.quote_id] = {
                    'quote_id': task.quote_id,
                    'job_name': task.job_name,
                    'customer_name': task.customer_name,
                    'task_count': 0,
                    'task_done_count': 0,
                    'task_started_count': 0,
                    'task_left_count': 0,
                    'all_done': true,
                    'expected_ship_date': task.expected_ship_date,
                    'production_state_date': this.productionStateDate(task),
                    'team_name': task.team_name,
                    'tasks': [],
                }
                jobOrder.push(task.quote_id)
            }
            const job = jobsById[task.quote_id]
            job.tasks.push(task)
            job.task_count++
            if (task.is_complete) {
                job.task_done_count++
            } else {
                job.all_done = false
                if (task.start_time) {
                    job.task_started_count++
                } else {
                    job.task_left_count++
                }
            }
        })

        Object.values(jobsById).forEach((job) => {
            job.last_updated_time = job.tasks
                .map((x) => [x.start_time, x.end_time])
                .flat()
                .filter(Boolean)
                .map((d) => new Date(d))
                .max()
        })

        const sortExtractor = GROUPED_SORT_EXTRACTORS[this.state.sort_column]
        if (sortExtractor) {
            // A column was explicitly clicked - this replaces the default
            // order entirely, same as QuotesViewController.js's order_by
            // (no merging with the default as a secondary key).
            jobOrder.sort((a, b) => {
                const av = sortExtractor(jobsById[a])
                const bv = sortExtractor(jobsById[b])
                if (av < bv) return this.state.sort_reverse ? 1 : -1
                if (av > bv) return this.state.sort_reverse ? -1 : 1
                return 0
            })
        } else {
            // Jobs with an unfinished task first, then by ship date within
            // each group - matches the done/started/not-started convention
            // JobScheduleFlowViewController.js's nodeStyle() already uses.
            jobOrder.sort((a, b) => {
                const jobA = jobsById[a], jobB = jobsById[b]
                if (jobA.all_done !== jobB.all_done) {
                    return jobA.all_done ? 1 : -1
                }
                return jobA.expected_ship_date - jobB.expected_ship_date
            })
        }

        return jobOrder.flatMap((quoteId) => {
            const job = jobsById[quoteId]
            const isExpanded = !!this.state.expandedJobIds[quoteId]
            const isMounted = !!this.state.mountedJobIds[quoteId]
            const last_updated_time = job.last_updated_time

            return [
                <tr key={'job-' + quoteId}>
                    <td>
                        <Button onClick={() => this.toggleJob(quoteId)}>
                            {isExpanded
                                ? <IoIosArrowDown style={{'fontSize': '14pt'}} />
                                : <IoIosArrowUp style={{'fontSize': '14pt'}} />
                            }
                        </Button>
                    </td>
                    <td><Label>
                        <a href={'/scheduler/' + quoteId} text={job.job_name}>{job.job_name}</a>
                    </Label></td>
                    <td><Label>
                        <a href={'/customerreport?filter=All&company=' + job.customer_name}>
                            {job.customer_name}
                        </a>
                    </Label></td>
                    <td><Label>
                        [<span style={{'color': 'var(--bs-success)'}}>{job.task_done_count}</span>,
                        {' '}<span style={{'color': 'var(--bs-warning)'}}>{job.task_started_count}</span>,
                        {' '}<span style={{'color': 'var(--bs-danger)'}}>
                            {job.task_left_count}
                        </span>]
                        /{job.task_count}
                    </Label></td>
                    <td><Label><span text={dateOffsetText(job.expected_ship_date)}>
                        {this.formatDate(job.expected_ship_date)}</span></Label></td>
                    <td><Label><span text={dateOffsetText(job.production_state_date)}>
                        {this.formatDate(job.production_state_date)}</span></Label></td>
                    <td><Label><span text={lastUpdatedTag(last_updated_time)}>
                        {last_updated_time ? dateFormat(last_updated_time, 'm/d/yyyy h:MM tt') : ''}
                    </span></Label></td>
                    <td><TeamDropdown
                        {...this.props}
                        quoteId={quoteId}
                        value={job.team_name}
                        teams={this.state.teams} /></td>
                </tr>,
                <tr key={'job-flow-' + quoteId}>
                    <td colSpan="8" style={{'padding': isExpanded ? '12px' : '0'}}>
                        <Collapse isOpen={isExpanded}>
                            {isMounted && <JobScheduleFlowViewController id={quoteId}
                                schedules={job.tasks.map(Schedule.from)} />}
                        </Collapse>
                    </td>
                </tr>,
            ]
        })
    }

    getFiles(quoteIds) {
        quoteIds = [...new Set(quoteIds)]
        getFilesFromManyQuoteIDs(quoteIds).then(
            (result) => {
                const files = this.state.files

                for (let i = 0; i < quoteIds.length; i++) {
                    files[quoteIds[i]] = []
                }

                for (let i = 0; i < result.data.files.length; i++) {
                    files[result.data.files[i].quote_id].push(result.data.files[i])
                }

                this.setState({
                    'files': files,
                })
                // this.props.turnOffLoader()
            },
            (error) => {
                this.props.showError(error)
                // this.props.turnOffLoader()
            }
        )
    }

    /**
     * A clickable, sortable <th> - same click/toggle/icon behavior as
     * QuotesViewController.js's makeTableHeaders(): first click sorts
     * ascending (fa-sort-down), clicking the same column again reverses
     * (fa-sort-up), clicking a different column resets to ascending.
     * Unlike Quotes this never re-fetches - it's a client-side sort over
     * the already-loaded task/job list.
     * @param {string} column key into UNGROUPED_SORT_EXTRACTORS/GROUPED_SORT_EXTRACTORS
     * @param {string} label
     * @param {object} thProps spread onto the <th> (className/style/etc.)
     * @return {JSX.Element}
     */
    makeSortableHeader(column, label, thProps = {}) {
        return <th {...thProps} onClick={() => this.toggleSort(column)}>
            {this.sortLabelContent(column, label)}
        </th>
    }

    /**
     * Flips/sets sort_column & sort_reverse the same way a click on a
     * makeSortableHeader() <th> does - factored out so a single <th> can
     * hold several independently-sortable labels (see the [Done, Started]
     * / Total Task / Left column), each needing its own click handler
     * without also toggling the others.
     * @param {string} column
     */
    toggleSort(column) {
        if (this.state.sort_column === column) {
            this.setState({ 'sort_reverse': !this.state.sort_reverse })
        } else {
            this.setState({ 'sort_column': column, 'sort_reverse': false })
        }
    }

    /**
     * The label text plus its sort icon (neutral fa-sort, or -up/-down
     * once this column is the active sort_column) - shared by
     * makeSortableHeader() and makeSortableLabel().
     * @param {string} column
     * @param {string|JSX.Element} label
     * @return {JSX.Element}
     */
    sortLabelContent(column, label) {
        let iconClass = 'fa fa-fw fa-sort'
        if (this.state.sort_column === column) {
            iconClass += this.state.sort_reverse ? '-up' : '-down'
        }
        return <>{label}<i className={iconClass}></i></>
    }

    /**
     * An inline, independently-clickable sortable label for use *inside*
     * a <th> that holds several sort targets at once (rather than one
     * sortable <th> per column). Stops propagation so clicking one label
     * doesn't also fire a click handler on an ancestor.
     * @param {string} column
     * @param {string} label
     * @return {JSX.Element}
     */
    makeSortableLabel(column, label) {
        return <span style={{'cursor': 'pointer'}} onClick={(e) => {
            e.stopPropagation()
            this.toggleSort(column)
        }}>{this.sortLabelContent(column, label)}</span>
    }

    /**
     * This will format the date
     * @param {Date} date
     * @return {string} format
     */
    formatDate(date) {
        if (!date || date.valueOf() <= 0) {
            return ''
        }
        return dateFormat(date.toDateTime(), 'mmmm d, yyyy')
    }

    /**
     * Picks the date matching whichever production-state filter is selected
     * @param {task} task
     * @return {JustDate}
     */
    productionStateDate(task) {
        switch (this.state.search_object.production_state) {
        case 'Ready For Production': return task.ready_for_production_date
        case 'In Production': return task.in_production_date
        case 'Thermoformed Signs Received': return task.thermoformed_signs_received_date
        default: return task.in_production_date.valueOf() > 0
            ? task.in_production_date
            : task.ready_for_production_date
        }
    }

    // MARK: Views

    renderHeader(label) {
        return <Col>
            <Label style={{
                'fontSize': '20pt',
                'fontWeight': '500',
            }}>{label}</Label>
        </Col>
    }

    /**
     * @param {string} title
     * @param {string} value
     * @param {[string]} values
     * @param {function} onClick
     * @param {{normalize: string}} modalOptions forwarded to
     *     `showTextFieldModal` when 'Other' is picked - see its docs.
     */
    renderDropDown(title, value, values, onClick, modalOptions={}) {
        // Where 'Other' sits in the caller's list. Captured before the
        // push below, which would otherwise shift it off the last slot and
        // make the onChange check miss it.
        const lastIndex = values.length - 1
        values = [...values]

        let index = values.indexOf(value)
        if (index === -1 && value.length > 0) {
            values.push(value)
            index = values.length - 1
        }

        return <>
            <Label style={{
                'fontSize': '18px',
                'fontWeight': '450',
            }}>{title}</Label>
            <SimpleDropdown
            index={index}
            values={values}
            onChange={(index) => {
                if (index === lastIndex &&
                    values[index] === 'Other') {
                    this.props.showTextFieldModal(title, (value) => {
                        onClick(value, index)
                    }, '', modalOptions)
                } else {
                    onClick(values[index], index)
                }
            }} />
        </>
    }

    renderDownloadButton(quoteId) {
        if (quoteId in this.state.error_files) {
            return <Label>Error loading</Label>
        }

        if (!(quoteId in this.state.files)) {
            return <Label>Loading ...</Label>
        }

        if (this.state.files[quoteId].length === 0) {
            return <Label>No files</Label>
        }

        return <SimpleDropdown
            index={-1}
            values={this.state.files[quoteId].map((file, index) => file.name)}
            onChange={(index) => {
                downloadFile(this.state.files[quoteId][index].id,
                    this.state.files[quoteId][index].name)
            }} />
    }

    renderPriorityDropDown(task, taskIndex) {

        const keys = Object.entries(Priority)
                .map((item) => item[0]).reverse()
        const values = Object.entries(Priority)
                .map((item) => item[1]).reverse()


        return <SimpleDropdown
            index={values.indexOf(task.priority)}
            values={keys}
            onChange={(index) => {
                const tasks = this.state.task
                tasks[taskIndex].priority = values[index]
                updateDailyScheduleWithBody(task.id, { priority: tasks[taskIndex].priority}).then(
                    (result) => {
                        //this.props.turnOffLoader()
                        this.patchFullTask(task.id, {'priority': tasks[taskIndex].priority})
                    },
                    (error) => {
                        this.props.showError(error)
                        //this.props.turnOffLoader()
                    }
                )

                this.setState({
                    'task': tasks,
                })
            }} />
    }

    renderAsignToDropDown(task, taskIndex) {

        const values = getSortedAssignTo(this.state.assign_to, task.equip)
        let index = values.indexOf(task.assign_to)
        if (index === -1 && task.assign_to.length > 0) {
            values.push(task.assign_to)
            index = values.length - 1
        }

        values.push('Other')

        function updateValue(ctx, value, index) {
            const tasks = ctx.state.task
            tasks[taskIndex].assign_to = value
            updateDailyScheduleWithBody(task.id,
                {assign_to:  value}).then(
                (result) => {
                    //this.props.turnOffLoader()
                    ctx.patchFullTask(task.id, {'assign_to': value})
                },
                (error) => {
                    ctx.props.showError(error)
                    //this.props.turnOffLoader()
                }
            )

            ctx.setState({
                'task': tasks,
            })
        }

        return <SimpleDropdown
            index={index}
            values={values}
            onChange={(index) => {
                if (index === values.length - 1 &&
                    values[index] === 'Other') {
                    this.props.showTextFieldModal('Assign To', (value) => {
                        updateValue(this, value, index)
                    }, '', {normalize: TextNormalization.Whitespace})
                } else {
                    updateValue(this, values[index], index)
                }
            }} />
    }

    /**
     * Looks up, from full_task, the tasks this task depends on (previous)
     * and the tasks that depend on this task (next) within the same job -
     * `dependency` holds order_index values, not ids (see Schedule.js).
     * @param {task} task
     * @return {{previous: [task], next: [task]}}
     */
    dependencyNeighbors(task) {
        const jobTasks = this.state.full_task.filter((t) => t.quote_id === task.quote_id)
        const byOrderIndex = {}
        jobTasks.forEach((t) => { byOrderIndex[t.order_index] = t })

        const previous = (task.dependency || [])
            .map((orderIndex) => byOrderIndex[orderIndex])
            .filter((t) => !!t)
        const next = jobTasks.filter((t) =>
            (t.dependency || []).includes(task.order_index))

        return { previous, next }
    }

    renderDependencyNeighbors(task) {
        const { previous, next } = this.dependencyNeighbors(task)
        if (previous.length === 0 && next.length === 0) {
            return null
        }
        return <div style={{'fontSize': '0.85em', 'opacity': 0.8}}>
            {previous.length > 0 &&
                <div>Previous: {previous.map((t) => t.short_description).join(', ')}</div>}
            {next.length > 0 &&
                <div>Next: {next.map((t) => t.short_description).join(', ')}</div>}
        </div>
    }

    makeRows() {
        // production_state_date depends on the current filter (this.state),
        // so it's not in the static UNGROUPED_SORT_EXTRACTORS map - handled
        // here instead.
        const sortExtractor = this.state.sort_column === 'production_state_date'
            ? (t) => this.productionStateDate(t).valueOf()
            : UNGROUPED_SORT_EXTRACTORS[this.state.sort_column]
        const tasks = !sortExtractor ? this.state.task : [...this.state.task].sort((a, b) => {
            const av = sortExtractor(a)
            const bv = sortExtractor(b)
            if (av < bv) return this.state.sort_reverse ? 1 : -1
            if (av > bv) return this.state.sort_reverse ? -1 : 1
            return 0
        })
        return tasks.flatMap((task, index) => {
            const isExpanded = !!this.state.expandedJobIds[task.id]
            const isMounted = !!this.state.mountedJobIds[task.id]

            const row = <tr key={"row"+index}>
                <td>{this.renderAsignToDropDown(task, index)}</td>
                <td>{this.renderPriorityDropDown(task, index)}</td>
                <td>{this.renderDownloadButton(task.quote_id)}</td>
                <td><Label>{task.equip}</Label></td>
                <td><Label>
                    <a href={'/scheduler/' + task.quote_id} text={task.job_name}
                    >{task.job_name}</a>
                </Label></td>
                <td><Label>
                    <a href={'/customerreport?filter=All&company=' + task.customer_name}>
                        {task.customer_name}
                    </a>
                </Label></td>
                <td><Label><span text={dateOffsetText(task.expected_ship_date)}>{this.formatDate(task.expected_ship_date)}</span></Label></td>
                <td><Label>{task.sign_given_ids ? task.sign_given_ids + '; ' : ''}{task.short_description}; {task.task}{this.renderDependencyNeighbors(task)}</Label></td>
                <td><Input
                    type='checkbox' checked={!!task.start_time}
                    onChange={(e) => {
                        // this.props.turnOnLoader()
                        const now = task.start_time ? null : new Date()
                        updateDailyScheduleWithBody(
                            task.id,
                            {
                                'start_time': now,
                            }
                        ).then(
                            (result) => {
                                //this.props.turnOffLoader()
                                const tasks = this.state.task
                                tasks[index].start_time = now

                                this.setState({
                                    'task': tasks,
                                })
                                this.patchFullTask(task.id, {'start_time': now})
                            },
                            (error) => {
                                this.props.showError(error)
                                //this.props.turnOffLoader()
                            }
                        )

                    }}/></td>
                <td><Input
                    type='checkbox' checked={task.is_complete}
                    onChange={(e) => {
                        // this.props.turnOnLoader()
                        const now = task.is_complete ? null : new Date()
                        updateDailyScheduleWithBody(
                            task.id,
                            {
                                'end_time': now,
                                'is_complete': !task.is_complete
                            }
                        ).then(
                            (result) => {
                                //this.props.turnOffLoader()
                                const tasks = this.state.task
                                tasks[index].end_time = now
                                tasks[index].is_complete = !task.is_complete

                                this.setState({
                                    'task': tasks,
                                })
                                this.patchFullTask(task.id, {
                                    'end_time': now,
                                    'is_complete': !task.is_complete,
                                })
                            },
                            (error) => {
                                console.log(error)
                                this.props.showError(error +
                                    '. This task may not be up to date and may require you to refresh the page.')
                                //this.props.turnOffLoader()
                            }
                        )

                    }}/></td>
                <td>
                    <Label>{formatTime(task.human_expected_duration_minutes)}</Label>
                </td>
                <td><Label><span text={dateOffsetText(this.productionStateDate(task))}>{this.formatDate(this.productionStateDate(task))}</span></Label></td>
                <td>
                    <Button onClick={() => this.toggleJob(task.id)}>
                        {isExpanded
                            ? <IoIosArrowDown style={{'fontSize': '14pt'}} />
                            : <IoIosArrowUp style={{'fontSize': '14pt'}} />
                        }
                    </Button>
                </td>
            </tr>

            return [
                row,
                <tr key={"row-flow-" + task.id}>
                    <td colSpan="13" style={{'padding': isExpanded ? '12px' : '0'}}>
                        <Collapse isOpen={isExpanded}>
                            {isMounted && <JobScheduleFlowViewController id={task.quote_id}
                                schedules={this.state.full_task
                                    .filter((t) => t.quote_id === task.quote_id)
                                    .map(Schedule.from)}
                                taskAnnotations={[{label: 'You are here', task_id: task.id}]} />}
                        </Collapse>
                    </td>
                </tr>,
            ]
        })
    }


    // MARK: View

    render() {
        const isGroupedByJob = this.state.group_by_job
        return (
            <Container>
                <Row>
                    <Col>
                        <Label style={{
                            'fontWeight': 'bold',
                            'fontSize': '18px',
                            'marginRight': '8px',
                        }}>Group By Job</Label>
                        <Input
                            type="checkbox"
                            checked={isGroupedByJob}
                            onChange={(e) => this.toggleGroupByJob()}
                            />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <InfoButton
                            message={
                                'This view consist of all task of jobs ' +
                                'that have the backlog "In Production" date ' +
                                'today or prior but not shipped.'
                            }
                            />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        {this.renderDropDown('Equip', this.state.search_object.search_task,
                            (() => {
                                const taskEquip = this.state.not_filter_task
                                    .map(t => t.equip)
                                    .filter(e => e && !EQUIPMENT_LIST.includes(e))
                                const extras = [...new Set(taskEquip)].sort()
                                const base = EQUIPMENT_LIST.filter(e => e !== 'Other')
                                return ['All', ...base, ...extras, 'Other']
                            })(), (value) => {
                                const searchObject = this.state.search_object
                                searchObject.search_task = value
                                this.setState({
                                    'search_object': searchObject,
                                })
                                this.props.search(searchObject)
                                // this.updateResults(searchObject)
                        })}
                    </Col>
                    <Col>
                        {this.renderDropDown('Production State', this.state.search_object.production_state,
                            [
                                'All', 'In Production', 'Ready For Production',
                                'Thermoformed Signs Received',
                            ], (value) => {
                                const searchObject = this.state.search_object
                                searchObject.production_state = value
                                this.setState({
                                    'search_object': searchObject,
                                })
                                this.props.search(searchObject)
                                // this.updateResults(searchObject)
                        })}
                    </Col>
                    <Col>
                        {this.renderDropDown('Completion', this.state.search_object.completion,
                            ['All', 'Completed', 'Not Completed'], (value) => {
                                const searchObject = this.state.search_object
                                searchObject.completion = value
                                this.setState({
                                    'search_object': searchObject,
                                })
                                this.props.search(searchObject)
                                // this.updateResults(searchObject)
                        })}
                    </Col>
                    <Col>
                        {this.renderDropDown('Assign To', this.state.search_object.assign_to,
                            ["All", ...getSortedAssignTo(this.state.assign_to), 'Other'], (value) => {
                                const searchObject = this.state.search_object
                                searchObject.assign_to = value
                                this.setState({
                                    'search_object': searchObject,
                                })
                                this.props.search(searchObject)
                                // this.updateResults(searchObject)
                        }, {normalize: TextNormalization.Whitespace})}
                    </Col>
                    <Col>
                        {(() => {
                            const { project_managers, search_object } = this.state
                            const currentPM = project_managers.find(p => p.id === search_object.pm)
                            const displayValue = currentPM
                                ? currentPM.first_name + ' ' + currentPM.last_name
                                : 'All'
                            return this.renderDropDown('Project Manager', displayValue,
                                ['All', ...project_managers.map(p => p.first_name + ' ' + p.last_name)],
                                (value) => {
                                    const searchObject = this.state.search_object
                                    if (value === 'All') {
                                        searchObject.pm = 'All'
                                    } else {
                                        const pm = project_managers.find(
                                            p => p.first_name + ' ' + p.last_name === value
                                        )
                                        searchObject.pm = pm ? pm.id : 'All'
                                    }
                                    this.setState({ 'search_object': searchObject })
                                    this.props.search(searchObject)
                                })
                        })()}
                    </Col>
                    <Col>
                        {this.renderDropDown('Team',
                            this.state.search_object.team === ''
                                ? 'Unassigned'
                                : this.state.search_object.team,
                            ['All', ...this.state.teams, 'Unassigned', 'Other'],
                            (value) => {
                                const searchObject = this.state.search_object
                                // Stored as '' - team_name's default - so the
                                // server never has to know the label.
                                searchObject.team =
                                    value === 'Unassigned' ? '' : value
                                this.setState({
                                    'search_object': searchObject,
                                })
                                this.props.search(searchObject)
                        }, {normalize: TextNormalization.Whitespace})}
                    </Col>
                    <Col>
                        {this.renderDropDown('Sign Type', this.state.search_object.sign_type,
                            ['All', ...this.state.sign_types], (value) => {
                                const searchObject = this.state.search_object
                                searchObject.sign_type = value
                                this.setState({
                                    'search_object': searchObject,
                                })
                                this.props.search(searchObject)
                        })}
                    </Col>
                    <Col>
                        {this.renderDropDown('Dependent', this.state.show_dependency,
                            ["All", "Completed", "Started"], (value) => {
                                this.setState({
                                    'show_dependency': value,
                                }, () => {
                                    this.filterDependency()
                                })
                        })}
                    </Col>
                </Row>
                <Row><Col><Container style={{'overflowX': 'auto'}}><Table>
                    {isGroupedByJob ? <thead>
                        <tr>
                            <th></th>
                            {this.makeSortableHeader('job_name', 'Job', {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                            {this.makeSortableHeader('customer_name', 'Customer Name', {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                            <th className="text-center label-weight"
                                style={{'minWidth': '100px'}}
                                title="Done, Started, Left / Total tasks - click a label to sort by it">
                                [{this.makeSortableLabel('task_done_count', 'Done')},
                                {' '}{this.makeSortableLabel('task_started_count', 'Started')},
                                {' '}{this.makeSortableLabel('task_left_count', 'Left')}]
                                /{this.makeSortableLabel('task_count', 'Total Task')}
                            </th>

                            {this.makeSortableHeader('expected_ship_date', 'Expected Ship Date', {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                            {this.makeSortableHeader('production_state_date', <>
                                {this.state.search_object.production_state !== 'All'
                                    ? this.state.search_object.production_state
                                    : 'Production State'} Date
                                {this.state.search_object.production_state === 'All' &&
                                    <span onClick={(e) => e.stopPropagation()}>
                                        <InfoButton
                                            id="production-state-date-grouped"
                                            message={
                                                'Shows the "In Production" date when set, ' +
                                                'otherwise falls back to the "Ready For Production" date.'
                                            }
                                            style={{marginLeft: '4px'}}
                                            />
                                    </span>
                                }
                            </>, {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                            {this.makeSortableHeader('last_updated_time', 'Last Updated', {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                            {this.makeSortableHeader('team_name', 'Team', {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                        </tr>
                    </thead> : <thead>
                        <tr>
                            <th className="text-center label-weight"
                                style={{'minWidth': '130px'}}>Assign To</th>
                            <th className="text-center label-weight"
                                style={{'minWidth': '130px'}}>Priority</th>
                            <th className="text-center label-weight"
                                style={{'minWidth': '80px'}}>Download</th>
                            {this.makeSortableHeader('equip', 'Equip', {
                                className: 'text-center label-weight',
                                style: {'minWidth': '130px'},
                            })}
                            {this.makeSortableHeader('job_name', 'Job', {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                            {this.makeSortableHeader('customer_name', 'Customer Name', {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                            {this.makeSortableHeader('expected_ship_date', 'Expected Ship Date', {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                            <th className="text-center label-weight"
                                style={{'minWidth': '150px'}}>Description</th>
                            <th className="text-center label-weight"
                                style={{'minWidth': '60px'}}>Started</th>
                            <th className="text-center label-weight"
                                style={{'minWidth': '60px'}}>Completion</th>
                            {this.makeSortableHeader('human_expected_duration_minutes',
                                'Human Expected Time', {
                                    className: 'text-center label-weight',
                                    style: {'minWidth': '60px'},
                                })}
                            {this.makeSortableHeader('production_state_date', <>
                                {this.state.search_object.production_state !== 'All'
                                    ? this.state.search_object.production_state
                                    : 'Production State'} Date
                                {this.state.search_object.production_state === 'All' &&
                                    <span onClick={(e) => e.stopPropagation()}>
                                        <InfoButton
                                            id="production-state-date"
                                            message={
                                                'Shows the "In Production" date when set, ' +
                                                'otherwise falls back to the "Ready For Production" date.'
                                            }
                                            style={{marginLeft: '4px'}}
                                            />
                                    </span>
                                }
                            </>, {
                                className: 'text-center label-weight',
                                style: {'minWidth': '150px'},
                            })}
                            <th></th>
                        </tr>
                    </thead>}
                    <tbody>
                        {isGroupedByJob ? this.makeJobScheduleRows() : this.makeRows()}
                    </tbody>
                </Table></Container></Col></Row>
            </Container>
        )
    }
}

export default DailyScheduleViewController
